import { FvButton, LoadMore, TextField } from '@fv/client-components'
import { addressFuncs } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'

import { HelpHrefButton } from '@/components/HelpHrefButton'
import InfoBox from '@/components/InfoBox'
import { InputAdornment } from '@/components/inputs/InputGroup'
import { List } from '@/components/List'
import { ShipperSlider } from '@/components/ShipperSlider'

import { HasPermission } from '../auth'
import { useHasPermission } from '../auth/hooks'
import { AddressBookEntry } from './AddressBookEntry'
import { AddressForm } from './AddressForm'
import { useAddressBook } from './useAddressBook'
import { useAddressesFromStore } from './useAddressBookAddresses'
import { useCacheFormattedAddress } from './useAddressSearch'

type SearchProps = {
  onUploadClick?: () => void
}
export const AddressBookListSearch = ({ onUploadClick }: SearchProps) => {
  const { setSearch, searchInput, showForm, searchText } = useAddressBook()
  const canManage = useHasPermission('manage-addresses')
  return (
    <div className="flex gap-x-2">
      <div className="input-group flex-1">
        <InputAdornment
          icon="search"
          iconClass="text-fv-green"
          position="start"
        />
        <TextField
          autoFocus
          className="form-control "
          name="addressSearch"
          onChange={searchInput}
          placeholder="Company, address or postal code"
          value={searchText}
        />
      </div>

      <HasPermission name="manage-addresses">
        <div>
          <FvButton icon="plus" onClick={showForm} theme="default">
            New address
          </FvButton>
        </div>
      </HasPermission>

      {onUploadClick && canManage && (
        <FvButton
          theme="default"
          onClick={() => {
            setSearch('')
            onUploadClick()
          }}
          icon="cloud-upload"
        >
          Upload
        </FvButton>
      )}

      <HelpHrefButton
        href="https://blog.freightview.com/utilizing-the-address-book"
        theme="btn"
      />
    </div>
  )
}

type ListProps = {
  inline?: boolean
  canRemove?: boolean
}
export const AddressBookList = ({ inline, canRemove }: ListProps) => {
  const addressBook = useAddressBook()
  const isBilling = addressBook.config.type === 'billing'
  const addressBookQuery = useAddressesFromStore(!!inline)
  const cacheFormattedAddress = useCacheFormattedAddress()
  function selectAddress(address: ClientAddressLookupResult) {
    addressBook.selectAddress?.(address)
    const formatted = addressFuncs.companyAddress(address)
    if (formatted) cacheFormattedAddress({ search: formatted }, address)
  }

  const addresses = addressBookQuery.data ?? []
  const mappedAddresses = addresses.map(a => ({
    ...a,
    isDupe:
      !!a.isAccountLocation &&
      !!a.accountLocationId &&
      !!addresses.filter(
        o =>
          o._id !== a._id &&
          o.isAccountLocation &&
          o.accountLocationId === a.accountLocationId,
      )?.length,
  }))
  return (
    <>
      {!addressBookQuery.data?.length &&
        (addressBookQuery.isLoading ? (
          <InfoBox icon="spinner">Loading...</InfoBox>
        ) : (
          <InfoBox>No addresses found.</InfoBox>
        ))}

      {mappedAddresses.map(a => (
        <AddressBookEntry
          address={a}
          key={a._id}
          selectAddress={addressBook.setAddress ? selectAddress : undefined}
          listHasRemove={canRemove}
          isRemovable={
            canRemove && (isBilling || !a.isAccountLocation || a.isDupe)
          }
        />
      ))}
      <div>
        <LoadMore
          className="mt-4"
          fetchNextPage={addressBookQuery.fetchNextPage}
          hasNextPage={addressBookQuery.hasNextPage}
          isLoading={addressBookQuery.isFetchingNextPage}
        />
      </div>
    </>
  )
}

export const AddressBookSlider = () => {
  const addressBook = useAddressBook()
  const addressBookQuery = useAddressesFromStore()

  const addressToEdit = addressBookQuery.data?.find(
    a => a._id === addressBook.editingId,
  )
  return (
    <ShipperSlider
      onClose={addressBook.close}
      isOpen={addressBook.isOpen}
      width="49rem"
      flush
    >
      {addressBook.view === 'list' && (
        <div className="p-6">
          {!addressBook.config.postalCodeFilter && (
            <div className="sticky top-0 z-10 -mx-6 -mt-6 border-b bg-white p-6 py-4">
              <AddressBookListSearch />
            </div>
          )}

          <List>
            <AddressBookList />
          </List>
        </div>
      )}

      {addressBook.view === 'form' && (
        <div className="p-6">
          <AddressForm
            config={addressBook.config}
            initialValues={addressToEdit}
          />
        </div>
      )}
    </ShipperSlider>
  )
}
